import { useState } from "react";
import { Target, User, Award, AlertTriangle } from "lucide-react";
import { usePublicData } from "../../hooks/usePublicData";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export default function PublicPlayerStats() {
  const { data, loading } = usePublicData();
  const [selectedTeam, setSelectedTeam] = useState("all");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) return null;

  const { player_stats, teams } = data;

  const topScorers = [...player_stats]
    .filter((p) => p.goals > 0)
    .sort((a, b) => b.goals - a.goals);

  const topAssists = [...player_stats]
    .filter((p) => p.assists > 0)
    .sort((a, b) => b.assists - a.assists);

  const mvpLeaders = [...player_stats]
    .filter((p) => p.mvp_awards > 0)
    .sort((a, b) => b.mvp_awards - a.mvp_awards);

  const mostCards = [...player_stats]
    .filter((p) => p.yellow_cards > 0 || p.red_cards > 0)
    .sort((a, b) => (b.yellow_cards + b.red_cards * 2) - (a.yellow_cards + a.red_cards * 2));

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold uppercase tracking-tight">
          Player Stats
        </h1>
        <p className="text-muted-foreground mt-1">
          Individual player statistics and rankings
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Top Scorers */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="font-heading text-lg font-bold uppercase mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" /> Top Scorers
          </h3>
          {topScorers.length > 0 ? (
            <div className="space-y-2">
              {topScorers.map((p, i) => (
                <div key={p.player_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary/50">
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold ${
                    i === 0 ? "bg-amber-500/20 text-amber-500" :
                    i === 1 ? "bg-gray-400/20 text-gray-400" :
                    i === 2 ? "bg-orange-600/20 text-orange-600" :
                    "bg-secondary text-muted-foreground"
                  }`}>
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{p.player_name}</p>
                    <p className="text-xs text-muted-foreground">{p.team_name}</p>
                  </div>
                  <span className="font-heading text-2xl font-bold text-primary">{p.goals}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm text-center py-4">No goals scored yet</p>
          )}
        </div>

        {/* Top Assists */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="font-heading text-lg font-bold uppercase mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-accent" /> Top Assists
          </h3>
          {topAssists.length > 0 ? (
            <div className="space-y-2">
              {topAssists.map((p, i) => (
                <div key={p.player_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary/50">
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold ${
                    i === 0 ? "bg-amber-500/20 text-amber-500" :
                    i === 1 ? "bg-gray-400/20 text-gray-400" :
                    i === 2 ? "bg-orange-600/20 text-orange-600" :
                    "bg-secondary text-muted-foreground"
                  }`}>
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{p.player_name}</p>
                    <p className="text-xs text-muted-foreground">{p.team_name}</p>
                  </div>
                  <span className="font-heading text-2xl font-bold text-accent">{p.assists}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm text-center py-4">No assists recorded yet</p>
          )}
        </div>

        {/* MVP Leaders */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="font-heading text-lg font-bold uppercase mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500" /> MVP Awards
          </h3>
          {mvpLeaders.length > 0 ? (
            <div className="space-y-2">
              {mvpLeaders.map((p, i) => (
                <div key={p.player_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary/50">
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold ${
                    i === 0 ? "bg-amber-500/20 text-amber-500" :
                    i === 1 ? "bg-gray-400/20 text-gray-400" :
                    i === 2 ? "bg-orange-600/20 text-orange-600" :
                    "bg-secondary text-muted-foreground"
                  }`}>
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{p.player_name}</p>
                    <p className="text-xs text-muted-foreground">{p.team_name}</p>
                  </div>
                  <span className="font-heading text-2xl font-bold text-amber-500">{p.mvp_awards}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm text-center py-4">No MVP awards yet</p>
          )}
        </div>

        {/* Discipline */}
        <div className="bg-card border border-border rounded-xl p-6 md:col-span-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <h3 className="font-heading text-lg font-bold uppercase flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" /> Discipline (Cards)
            </h3>
            <Select value={selectedTeam} onValueChange={setSelectedTeam}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="team-selector">
                <SelectValue placeholder="Select team" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Teams</SelectItem>
                {teams?.map((team) => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Team Summary */}
          {selectedTeam !== "all" && (() => {
            const teamPlayers = mostCards.filter(p => p.team_id === selectedTeam);
            const teamYellows = teamPlayers.reduce((sum, p) => sum + (p.yellow_cards || 0), 0);
            const teamReds = teamPlayers.reduce((sum, p) => sum + (p.red_cards || 0), 0);
            const teamName = teams?.find(t => t.id === selectedTeam)?.name || "Team";
            const yellowCost = 2.5;
            const redCost = 5;
            const totalFines = (teamYellows * yellowCost) + (teamReds * redCost);

            return (
              <div className="bg-secondary/30 rounded-lg p-4 mb-4">
                <h4 className="font-bold text-lg mb-2">{teamName} - Card Summary</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <span className="w-5 h-6 bg-amber-500 rounded-sm" />
                    </div>
                    <p className="text-2xl font-bold text-amber-500">{teamYellows}</p>
                    <p className="text-xs text-muted-foreground">Yellow Cards</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <span className="w-5 h-6 bg-red-500 rounded-sm" />
                    </div>
                    <p className="text-2xl font-bold text-red-500">{teamReds}</p>
                    <p className="text-xs text-muted-foreground">Red Cards</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold">{teamYellows + teamReds}</p>
                    <p className="text-xs text-muted-foreground">Total Cards</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-500">${totalFines.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">Total Fines</p>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Players List */}
          {(() => {
            const filteredPlayers = selectedTeam === "all" 
              ? mostCards 
              : mostCards.filter(p => p.team_id === selectedTeam);
            
            return filteredPlayers.length > 0 ? (
              <div className="space-y-2">
                {filteredPlayers.map((p, i) => {
                  const yellowCost = 2.5;
                  const redCost = 5;
                  const playerFines = ((p.yellow_cards || 0) * yellowCost) + ((p.red_cards || 0) * redCost);
                  
                  return (
                    <div key={p.player_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary/50">
                      <span className="w-8 h-8 rounded-lg flex items-center justify-center font-bold bg-secondary text-muted-foreground">
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{p.player_name}</p>
                        <p className="text-xs text-muted-foreground">{p.team_name}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        {p.yellow_cards > 0 && (
                          <span className="flex items-center gap-1">
                            <span className="w-4 h-5 bg-amber-500 rounded-sm" />
                            <span className="text-sm font-medium">{p.yellow_cards}</span>
                          </span>
                        )}
                        {p.red_cards > 0 && (
                          <span className="flex items-center gap-1">
                            <span className="w-4 h-5 bg-red-500 rounded-sm" />
                            <span className="text-sm font-medium">{p.red_cards}</span>
                          </span>
                        )}
                        <span className="text-sm text-green-500 font-medium min-w-[60px] text-right">
                          ${playerFines.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm text-center py-4">No cards issued yet</p>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
